import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompany, ILocation } from '../../../redux/company/types';
import { COMPANY_TYPES } from '../../../data/forms/company.data';
import { useDispatch } from 'react-redux';
import { updateCompany } from '../../../redux/company/actions';
import PictureUploader from '../../PictureUploader/PictureUploader';
import { CustomDatePicker } from '../../DatePicker/CustomDatePicker';
import LocationAutocomplete from '../../LocationAutocomplete/LocationAutocomplete';
import dayjs from 'dayjs';
import { currencySymbols } from '../../../constants/currency';

interface ICompanyBasicInfoEdit {
    company?: ICompany;
    isEditing: boolean;
    onSave: (saveFunction: () => void) => void;
    onChanges: (hasChanges: boolean) => void;
}

const CompanyBasicInfoEdit: React.FC<ICompanyBasicInfoEdit> = ({ company, isEditing, onSave, onChanges }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        company_name: company?.company_name || '',
        company_type: company?.company_type || 'media company',
        date_of_formation: company?.date_of_formation ? dayjs(company.date_of_formation) : null,
        min_employees: company?.min_employees || 1,
        max_employees: company?.max_employees || 10,
        min_revennue: company?.min_revennue || 1,
        max_revenue: company?.max_revenue || 1000,
        currency: company?.currency || 'USD',
        profile_picture: company?.profile_picture || '',
        rawProfilePicture: {},
        location: company?.location || { address: '', description: '', coordinates: [], type: 'Point' },
    });
    const [initialData, setInitialData] = useState(formData);

    useEffect(() => {
        if (company) {
            const newData = {
                company_name: company.company_name || '',
                company_type: company.company_type || 'media company',
                date_of_formation: company.date_of_formation ? dayjs(company.date_of_formation) : null,
                min_employees: company.min_employees || 1,
                max_employees: company.max_employees || 10,
                min_revennue: company.min_revennue || 1,
                max_revenue: company.max_revenue || 1000,
                currency: company.currency || 'USD',
                profile_picture: company.profile_picture || '',
                rawProfilePicture: {},
                location: company.location || { address: '', description: '', coordinates: [], type: 'Point' },
            };
            setFormData(newData);
            setInitialData(newData);
        }
    }, [company]);

    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialData);
        onChanges(hasChanges);
    }, [formData, initialData, onChanges]);

    const handleFieldChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSave = () => {
        if (company?._id) {
            const payload: any = {
                id: company._id,
                body: {
                    company_name: formData.company_name,
                    company_type: formData.company_type,
                    ...(formData.date_of_formation && { date_of_formation: formData.date_of_formation.toISOString() }),
                    min_employees: formData.min_employees,
                    max_employees: formData.max_employees,
                    min_revennue: formData.min_revennue,
                    max_revenue: formData.max_revenue,
                    currency: formData.currency,
                    location: formData.location,
                    profile_picture: formData.rawProfilePicture,
                },
            };
            dispatch(updateCompany(payload));
        }
    };

    // Register the save function with the parent container
    useEffect(() => {
        onSave(handleSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave, formData, company?._id]);

    const handleImageChange = (
        image: string | any[] | null,
        rawFile?: File[] | FileList | null,
        alternateKey?: string,
    ) => {
        handleFieldChange('profile_picture', image);
        handleFieldChange('rawProfilePicture', rawFile);
    };

    const handleLocationSelect = (address: string, coordinates?: number[], formattedAddress?: ILocation) => {
        if (formattedAddress) {
            handleFieldChange('location', formattedAddress);
        } else {
            handleFieldChange('location', {
                ...formData.location,
                address,
                coordinates: coordinates || [],
            });
        }
    };

    return (
        <Card sx={{ bgcolor: theme[themeStyle].secondaryBg, border: `1px solid ${theme[themeStyle].tag_white_light}` }}>
            <CardHeader
                title="Basic Information"
                sx={{ color: theme[themeStyle].tag_white }}
                titleTypographyProps={{
                    sx: { fontFamily: FONT_ENUM.MAIN_BOLD },
                }}
            />
            <CardContent>
                <Grid container spacing={3}>
                    {/* Company Name */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Company Name"
                            value={formData.company_name}
                            onChange={e => handleFieldChange('company_name', e.target.value)}
                            disabled={!isEditing}
                            sx={{
                                '& .MuiInputBase-input': {
                                    color: theme[themeStyle].tag_white,
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].lightWhite,
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                            }}
                        />
                    </Grid>

                    {/* Company Type */}
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Company Type</InputLabel>
                            <Select
                                value={formData.company_type}
                                onChange={e => handleFieldChange('company_type', e.target.value)}
                                disabled={!isEditing}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: theme[themeStyle].dropdown_black,
                                            border: `1px solid ${theme[themeStyle].form_divider}`,
                                            borderRadius: '8px',
                                            '& .MuiMenuItem-root': {
                                                color: theme[themeStyle].primary_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '&:hover': {
                                                    backgroundColor: theme[themeStyle].form_divider,
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: theme[themeStyle].highlight,
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].highlight,
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}
                                sx={{
                                    '& .MuiSelect-select': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: theme[themeStyle].primary_white,
                                    },
                                }}>
                                {COMPANY_TYPES.map(type => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Date of Formation */}
                    <Grid item xs={12} md={6}>
                        <CustomDatePicker
                            label="Date of Formation"
                            value={formData.date_of_formation ? formData.date_of_formation.toISOString() : undefined}
                            onChange={date => handleFieldChange('date_of_formation', date ? dayjs(date) : null)}
                            disabled={!isEditing}
                            withTimezone={false}
                            sx={{
                                backgroundColor: theme[themeStyle].gray_text,
                                '&:focus-within': {
                                    border: `1px solid ${theme[themeStyle].highlight}`,
                                },
                            }}
                        />
                    </Grid>

                    {/* Location */}
                    <Grid item xs={12}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                mb: 2,
                            }}>
                            Company Headquaters
                        </Typography>
                        <LocationAutocomplete
                            value={{
                                address: formData.location.address || '',
                                description: formData.location.description || '',
                                coordinates: formData.location.coordinates || [],
                            }}
                            onSelect={handleLocationSelect}
                            onSelectLocationOption={() => {}}
                            placeholder="Search for company location..."
                            withCordinates
                            searchOptions={{ types: ['(cities)'] }}
                            fieldStyle={{
                                bgcolor: theme[themeStyle].secondaryBg,
                                borderRadius: '8px',
                                input: { color: theme[themeStyle].tag_white },
                                marginTop: '0px',
                                label: { color: theme[themeStyle].tag_white },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme[themeStyle].tag_white,
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme[themeStyle].tag_white,
                                },
                                '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme[themeStyle].tag_white,
                                },
                            }}
                        />
                    </Grid>

                    {/* Employee Range */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Minimum Employees"
                            type="number"
                            value={formData.min_employees}
                            onChange={e => handleFieldChange('min_employees', parseInt(e.target.value) || 1)}
                            disabled={!isEditing}
                            sx={{
                                '& .MuiInputBase-input': {
                                    color: theme[themeStyle].tag_white,
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].lightWhite,
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Maximum Employees"
                            type="number"
                            value={formData.max_employees}
                            onChange={e => handleFieldChange('max_employees', parseInt(e.target.value) || 10)}
                            disabled={!isEditing}
                            sx={{
                                '& .MuiInputBase-input': {
                                    color: theme[themeStyle].tag_white,
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].lightWhite,
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                            }}
                        />
                    </Grid>

                    {/* Revenue Range */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Minimum Revenue"
                            type="number"
                            value={formData.min_revennue}
                            onChange={e => handleFieldChange('min_revennue', parseInt(e.target.value) || 1)}
                            disabled={!isEditing}
                            sx={{
                                '& .MuiInputBase-input': {
                                    color: theme[themeStyle].tag_white,
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].lightWhite,
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Maximum Revenue"
                            type="number"
                            value={formData.max_revenue}
                            onChange={e => handleFieldChange('max_revenue', parseInt(e.target.value))}
                            disabled={!isEditing}
                            sx={{
                                '& .MuiInputBase-input': {
                                    color: theme[themeStyle].tag_white,
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].lightWhite,
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: theme[themeStyle].tag_white }}>Currency</InputLabel>
                            <Select
                                value={formData.currency}
                                onChange={e => handleFieldChange('currency', e.target.value)}
                                disabled={!isEditing}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: theme[themeStyle].dropdown_black,
                                            border: `1px solid ${theme[themeStyle].form_divider}`,
                                            borderRadius: '8px',
                                            '& .MuiMenuItem-root': {
                                                color: theme[themeStyle].primary_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                '&:hover': {
                                                    backgroundColor: theme[themeStyle].form_divider,
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: theme[themeStyle].highlight,
                                                    color: theme[themeStyle].primary_white,
                                                    '&:hover': {
                                                        backgroundColor: theme[themeStyle].highlight,
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}
                                sx={{
                                    '& .MuiSelect-select': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: theme[themeStyle].primary_white,
                                    },
                                }}>
                                {Object.keys(currencySymbols).map(cur => (
                                    <MenuItem key={cur} value={cur}>
                                        {cur}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Profile Picture */}
                    <Grid item xs={12}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                mb: 2,
                            }}>
                            Profile Picture
                        </Typography>
                        <PictureUploader
                            initialImageSrc={formData.profile_picture}
                            onChange={handleImageChange}
                            onRemove={() => handleImageChange(null)}
                            aspect={1}
                            sx={{ width: '100%', maxWidth: '300px' }}
                            isEditMode={isEditing}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export { CompanyBasicInfoEdit };
